import type { ClintplayEvent, NetworkEventData } from '../types';

type EventCallback = (event: ClintplayEvent) => void;

const MAX_BODY_SIZE = 1024 * 1024; // 1MB limit per body

// Match the SaaS-hosted ingest hostname pattern as a fallback when no
// explicit apiUrl is available (e.g., XHR opened before the SDK init).
const SAAS_HOST_PATTERN = /(^|\.)clintplay\./;

function isSelfIngestUrl(url: string, ingestHost: string | null): boolean {
  // Self-hosted: match the configured apiUrl's host exactly.
  // SaaS: also match any *.clintplay.* host so the regex still helps.
  try {
    const u = new URL(url, globalThis.location?.href ?? 'http://localhost');
    if (ingestHost && u.host === ingestHost) return true;
    if (SAAS_HOST_PATTERN.test(u.host)) return true;
  } catch {
    // Relative URLs that fail to parse — assume external.
  }
  return false;
}

export class NetworkInterceptor {
  private originalFetch: typeof fetch | null = null;
  private originalXHROpen: typeof XMLHttpRequest.prototype.open | null = null;
  private originalXHRSend: typeof XMLHttpRequest.prototype.send | null = null;
  private active = false;
  private ingestHost: string | null;

  constructor(
    private sessionId: string,
    private emit: EventCallback,
    apiUrl?: string
  ) {
    // Pre-compute the ingest host so the hot-path check is just a
    // string compare instead of a URL parse on every fetch/XHR.
    let host: string | null = null;
    if (apiUrl) {
      try {
        host = new URL(apiUrl).host;
      } catch {
        host = null;
      }
    }
    this.ingestHost = host;
  }

  start(): void {
    this.active = true;
    this.patchFetch();
    this.patchXHR();
  }

  stop(): void {
    this.active = false;
    this.restoreFetch();
    this.restoreXHR();
  }

  private patchFetch(): void {
    this.originalFetch = globalThis.fetch;
    const self = this;

    globalThis.fetch = async function (
      input: RequestInfo | URL,
      init?: RequestInit
    ): Promise<Response> {
      const url =
        typeof input === 'string'
          ? input
          : input instanceof URL
            ? input.toString()
            : input.url;
      const method = init?.method ?? 'GET';

      if (isSelfIngestUrl(url, self.ingestHost)) {
        return self.originalFetch!.call(globalThis, input, init);
      }

      const startTime = Date.now();

      // Extract request headers
      const requestHeaders = extractHeaders(init?.headers);

      // Extract request body
      const requestBody = extractBody(init?.body);

      try {
        const response = await self.originalFetch!.call(
          globalThis,
          input,
          init
        );

        if (self.active) {
          // Extract response headers
          const responseHeaders: Record<string, string> = {};
          response.headers.forEach((value, key) => {
            responseHeaders[key] = value;
          });

          // Clone response to read body without consuming the original
          let responseBody: string | undefined;
          try {
            const contentType = response.headers.get('content-type') ?? '';
            if (contentType.includes('json') || contentType.includes('text')) {
              const clone = response.clone();
              const text = await clone.text();
              responseBody = text.length > MAX_BODY_SIZE
                ? text.substring(0, MAX_BODY_SIZE) + '...[truncated]'
                : text;
            }
          } catch {
            // Body read failed — skip
          }

          const data: NetworkEventData = {
            method: method.toUpperCase(),
            url,
            status: response.status,
            statusText: response.statusText,
            requestHeaders,
            responseHeaders,
            requestBody,
            responseBody,
            startTime,
            endTime: Date.now(),
            duration: Date.now() - startTime,
          };

          self.emit({
            type: 'network',
            timestamp: startTime,
            sessionId: self.sessionId,
            data,
          });

          // Emit failed HTTP responses (4xx/5xx) as errors too
          if (response.status >= 400) {
            self.emit({
              type: 'error',
              timestamp: Date.now(),
              sessionId: self.sessionId,
              data: {
                message: `HTTP ${response.status} ${response.statusText}: ${method.toUpperCase()} ${url}`,
                type: 'onerror',
                source: 'network-interceptor',
              },
            });
          }
        }

        return response;
      } catch (error) {
        if (self.active) {
          const endTime = Date.now();
          const data: NetworkEventData = {
            method: method.toUpperCase(),
            url,
            requestHeaders,
            requestBody,
            startTime,
            endTime,
            duration: endTime - startTime,
          };

          self.emit({
            type: 'network',
            timestamp: startTime,
            sessionId: self.sessionId,
            data,
          });

          // Also emit as an error so the session gets flagged
          const errMsg = error instanceof Error ? error.message : String(error);
          self.emit({
            type: 'error',
            timestamp: endTime,
            sessionId: self.sessionId,
            data: {
              message: `Network error: ${method.toUpperCase()} ${url} — ${errMsg}`,
              type: 'onerror',
              source: 'network-interceptor',
            },
          });
        }

        throw error;
      }
    };
  }

  private restoreFetch(): void {
    if (this.originalFetch) {
      globalThis.fetch = this.originalFetch;
      this.originalFetch = null;
    }
  }

  private patchXHR(): void {
    this.originalXHROpen = XMLHttpRequest.prototype.open;
    this.originalXHRSend = XMLHttpRequest.prototype.send;
    const self = this;

    XMLHttpRequest.prototype.open = function (
      this: XMLHttpRequest,
      method: string,
      url: string | URL,
      ...rest: any[]
    ) {
      (this as any).__gp_method = method;
      (this as any).__gp_url = typeof url === 'string' ? url : url.toString();
      return self.originalXHROpen!.apply(this, [method, url, ...rest] as any);
    };

    XMLHttpRequest.prototype.send = function (
      this: XMLHttpRequest,
      body?: Document | XMLHttpRequestBodyInit | null
    ) {
      const method: string = (this as any).__gp_method ?? 'GET';
      const url: string = (this as any).__gp_url ?? '';

      if (isSelfIngestUrl(url, self.ingestHost)) {
        return self.originalXHRSend!.call(this, body);
      }

      const startTime = Date.now();

      // Extract request body
      const requestBody = typeof body === 'string' ? extractBody(body) : undefined;

      // Extract request headers after they've been set via setRequestHeader
      // (headers are only available on the response side for XHR)

      const emitNetworkEvent = (status?: number, statusText?: string) => {
        if (!self.active) return;

        // Extract response headers
        const responseHeaders: Record<string, string> = {};
        const allHeaders = this.getAllResponseHeaders();
        if (allHeaders) {
          for (const line of allHeaders.trim().split(/[\r\n]+/)) {
            const idx = line.indexOf(': ');
            if (idx > 0) {
              responseHeaders[line.substring(0, idx).toLowerCase()] = line.substring(idx + 2);
            }
          }
        }

        // Extract response body
        let responseBody: string | undefined;
        try {
          if (this.responseType === '' || this.responseType === 'text') {
            const text = this.responseText;
            responseBody = text.length > MAX_BODY_SIZE
              ? text.substring(0, MAX_BODY_SIZE) + '...[truncated]'
              : text;
          }
        } catch {
          // Body read failed — skip
        }

        const endTime = Date.now();
        const data: NetworkEventData = {
          method: method.toUpperCase(),
          url,
          status,
          statusText,
          requestBody,
          responseHeaders,
          startTime,
          endTime,
          duration: endTime - startTime,
        };

        self.emit({
          type: 'network',
          timestamp: startTime,
          sessionId: self.sessionId,
          data,
        });

        // Emit HTTP 4xx/5xx as errors
        if (status !== undefined && status >= 400) {
          self.emit({
            type: 'error',
            timestamp: endTime,
            sessionId: self.sessionId,
            data: {
              message: `HTTP ${status} ${statusText ?? ''}: ${method.toUpperCase()} ${url}`,
              type: 'onerror',
              source: 'network-interceptor',
            },
          });
        }
      };

      const emitFailureEvent = (reason: string) => {
        if (!self.active) return;

        const endTime = Date.now();
        const data: NetworkEventData = {
          method: method.toUpperCase(),
          url,
          requestBody,
          startTime,
          endTime,
          duration: endTime - startTime,
        };

        self.emit({
          type: 'network',
          timestamp: startTime,
          sessionId: self.sessionId,
          data,
        });

        self.emit({
          type: 'error',
          timestamp: endTime,
          sessionId: self.sessionId,
          data: {
            message: `Network ${reason}: ${method.toUpperCase()} ${url}`,
            type: 'onerror',
            source: 'network-interceptor',
          },
        });
      };

      this.addEventListener('load', () => {
        emitNetworkEvent(this.status, this.statusText);
      });

      this.addEventListener('error', () => {
        emitFailureEvent('error');
      });

      this.addEventListener('timeout', () => {
        emitFailureEvent('timeout');
      });

      this.addEventListener('abort', () => {
        emitFailureEvent('abort');
      });

      return self.originalXHRSend!.call(this, body);
    };
  }

  private restoreXHR(): void {
    if (this.originalXHROpen) {
      XMLHttpRequest.prototype.open = this.originalXHROpen;
      this.originalXHROpen = null;
    }
    if (this.originalXHRSend) {
      XMLHttpRequest.prototype.send = this.originalXHRSend;
      this.originalXHRSend = null;
    }
  }
}

function extractHeaders(headers: HeadersInit | undefined): Record<string, string> {
  const result: Record<string, string> = {};
  if (!headers) return result;

  if (headers instanceof Headers) {
    headers.forEach((value, key) => { result[key] = value; });
  } else if (Array.isArray(headers)) {
    for (const [key, value] of headers) { result[key] = value; }
  } else {
    for (const [key, value] of Object.entries(headers)) { result[key] = value; }
  }

  return result;
}

function extractBody(body: BodyInit | null | undefined): string | undefined {
  if (!body) return undefined;

  if (typeof body === 'string') {
    return body.length > MAX_BODY_SIZE
      ? body.substring(0, MAX_BODY_SIZE) + '...[truncated]'
      : body;
  }

  if (body instanceof URLSearchParams) {
    return body.toString();
  }

  // FormData, Blob, ArrayBuffer, ReadableStream — skip (too complex/binary)
  return undefined;
}
